import { itemizeBookingReport, totalBookingHours } from '../utils/itemizeBookingReport';
import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, CircleDollarSign, Download, Filter, Grid3X3, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getBookingDetailsFilterOptions, getBookingDetailsReport } from '../services/reportService';
import { formatCurrency, formatDate, formatTime } from '../utils/formatters';

const philippineDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Manila', year: 'numeric', month: '2-digit', day: '2-digit',
});

function philippineDateValue() {
  const parts = Object.fromEntries(philippineDateFormatter.formatToParts(new Date()).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function createDateRange() {
  const today = philippineDateValue();
  return { start: today, end: today };
}

function courtDisplayName(name) {
  const value = String(name || '').trim();
  return /^\d+$/.test(value) ? `Court ${value}` : value;
}

function csvCell(value) {
  const stringValue = String(value ?? '');
  return /[",\n]/.test(stringValue) ? `"${stringValue.replaceAll('"', '""')}"` : stringValue;
}

function statusClass(status) {
  switch (String(status || '').toLowerCase()) {
    case 'confirmed':
    case 'checkedin':
      return 'bg-[#DCFCE7] text-[#166534]';
    case 'completed':
      return 'bg-[#DBEAFE] text-[#1D4ED8]';
    case 'cancelled':
      return 'bg-[#FEE2E2] text-[#B42318]';
    default:
      return 'bg-[#FEF3C7] text-[#B45309]';
  }
}

function bookingDateValue(value) {
  return philippineDateFormatter.format(new Date(value));
}

function CourtSchedule({ bookings, courts, onOpenBooking }) {
  const rows = useMemo(() => {
    const slots = new Map();
    bookings.forEach((booking) => {
      const key = `${bookingDateValue(booking.startTime)}|${booking.startTime}|${booking.endTime}`;
      if (!slots.has(key)) slots.set(key, { date: bookingDateValue(booking.startTime), startTime: booking.startTime, endTime: booking.endTime, bookings: new Map() });
      const courtBookings = slots.get(key).bookings.get(booking.courtId) || [];
      if (!courtBookings.some((value) => value.id === booking.id)) courtBookings.push(booking);
      slots.get(key).bookings.set(booking.courtId, courtBookings);
    });
    return [...slots.values()].sort((first, second) => new Date(first.startTime) - new Date(second.startTime));
  }, [bookings]);

  if (!courts.length) return <div className="p-12 text-center text-sm text-[#64748B]">No courts are available for this venue.</div>;
  return <div className="overflow-x-auto">
    <table className="w-full min-w-max border-separate border-spacing-0 text-left text-sm">
      <thead className="sticky top-0 z-20 bg-[#263238] text-white"><tr><th className="sticky left-0 z-30 min-w-[165px] border-r border-white/10 bg-[#263238] px-4 py-4">Date</th><th className="min-w-[175px] border-r border-white/10 px-4 py-4">Time Slot</th>{courts.map((court) => <th key={court.id} className="min-w-[245px] border-r border-white/10 px-4 py-4">{courtDisplayName(court.name)}</th>)}</tr></thead>
      <tbody>{rows.map((row) => <tr key={`${row.date}-${row.startTime}`} className="align-top">
        <td className="sticky left-0 z-10 border-b border-r border-[#CBD5E1] bg-white px-4 py-4 font-semibold text-[#344054]">{new Date(row.startTime).toLocaleDateString('en-PH', { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' })}</td>
        <td className="whitespace-nowrap border-b border-r border-[#CBD5E1] bg-white px-4 py-4 font-semibold text-[#344054]">{formatTime(row.startTime)}–{formatTime(row.endTime)}</td>
        {courts.map((court) => {
          const cellBookings = row.bookings.get(court.id) || [];
          return <td key={court.id} className="border-b border-r border-[#CBD5E1] bg-white p-0">{cellBookings.length ? <div className="divide-y divide-[#E2E8F0]">{cellBookings.map((booking) => {
            const canOpen = booking.bookingType === 'Court Booking';
            return <button key={booking.id} type="button" disabled={!canOpen} onClick={() => canOpen && onOpenBooking(booking.id)} className={`block min-h-[118px] w-full p-4 text-left ${canOpen ? 'hover:bg-[#F8FAFC]' : 'cursor-default'}`}><p className="font-semibold text-[#1F2937]">{booking.openPlayName || booking.playerName || 'Player'}</p><p className="mt-1 text-xs text-[#475569]">{booking.status || '—'} | {booking.paymentMethod || '—'} ({booking.paymentStatus || '—'})</p><p className="mt-2 font-bold text-[#1F2937]">{formatCurrency(booking.rentalAmount)}</p>{booking.purchasedAt && <p className="mt-1 text-[10px] text-[#94A3B8]">Purchased {formatDate(booking.purchasedAt)} · {formatTime(booking.purchasedAt)}</p>}</button>;
          })}</div> : <div className="min-h-[104px] p-4 text-xs font-medium italic text-[#94A3B8]">AVAILABLE</div>}</td>;
        })}
      </tr>)}</tbody>
    </table>
    {!rows.length && <div className="p-12 text-center text-sm text-[#64748B]">No bookings match the selected period and filters.</div>}
  </div>;
}

export default function BookingDetailsReport() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('booking-details-view') || 'matrix');
  const [dateRange, setDateRange] = useState(createDateRange);
  const [venueId, setVenueId] = useState('');
  const [courtId, setCourtId] = useState('');
  const [activityType, setActivityType] = useState('All');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [bookingStatus, setBookingStatus] = useState('active');
  const [courts, setCourts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bookingStatuses, setBookingStatuses] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  async function loadFilters() {
    try {
      const data = await getBookingDetailsFilterOptions();
      setCourts(data?.courts || []);
      setPaymentMethods(data?.paymentMethods || []);
      setBookingStatuses(data?.bookingStatuses || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load report filters');
    }
  }

  async function loadReport(selectedRange = dateRange, overrides = {}) {
    if (selectedRange.end < selectedRange.start) {
      toast.error('End date must be on or after the start date');
      return;
    }
    setLoading(true);
    try {
      const data = await getBookingDetailsReport({
        startDate: selectedRange.start,
        endDate: selectedRange.end,
        ...((overrides.venueId ?? venueId) ? { venueId: overrides.venueId ?? venueId } : {}),
        ...(courtId ? { courtId } : {}),
        ...(paymentMethod ? { paymentMethod } : {}),
        bookingStatus,
        activityType,
      });
      setReport(data);
    } catch (error) {
      setReport(null);
      toast.error(error.response?.data?.message || 'Failed to load booking details');
    } finally {
      setLoading(false);
    }
  }

  function showToday() {
    const today = philippineDateValue();
    const todayRange = { start: today, end: today };
    setDateRange(todayRange);
    loadReport(todayRange);
  }

  function changeView(nextView) {
    localStorage.setItem('booking-details-view', nextView);
    setViewMode(nextView);
    if (nextView === 'matrix') {
      const nextVenue = venueId || venues[0]?.id || '';
      setVenueId(nextVenue); setCourtId('');
      loadReport(dateRange, { venueId: nextVenue });
    }
  }

  useEffect(() => {
    async function initialise() {
      try {
        const data = await getBookingDetailsFilterOptions();
        const loadedCourts = data?.courts || [];
        setCourts(loadedCourts); setPaymentMethods(data?.paymentMethods || []); setBookingStatuses(data?.bookingStatuses || []);
        if (viewMode === 'matrix') {
          const firstVenue = loadedCourts[0]?.venueId || '';
          const range = createDateRange();
          setVenueId(firstVenue);
          await loadReport(range, { venueId: firstVenue });
        } else await loadReport();
      } catch (error) { toast.error(error.response?.data?.message || 'Failed to load report'); setLoading(false); }
    }
    initialise();
  }, []);

  const venues = useMemo(() => {
    const values = new Map();
    courts.forEach((court) => values.set(court.venueId, court.venueName));
    return [...values.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((first, second) => first.name.localeCompare(second.name));
  }, [courts]);

  const filteredCourts = useMemo(
    () => courts.filter((court) => !venueId || court.venueId === venueId),
    [courts, venueId],
  );
  const bookingHours = useMemo(() => totalBookingHours(report?.bookings || []), [report]);
  const itemizedBookings = useMemo(() => itemizeBookingReport(report?.bookings || []), [report]);
  const showConvenienceFee = useMemo(
    () => (report?.bookings || []).some((booking) => booking.convenienceFeeCoveredByVenue),
    [report],
  );

  function exportReport() {
    if (!report) return;
    const rows = [
      ['FREAC booking details report'],
      ['Period', `${dateRange.start} to ${dateRange.end}`],
      ['Venue', venues.find((venue) => venue.id === venueId)?.name || 'All venues'],
      ['Court', courtDisplayName(filteredCourts.find((court) => court.id === courtId)?.name) || 'All courts'],
      ['Payment method', paymentMethod || 'All payment methods'],
      ['Booking status', bookingStatuses.find((status) => status.value === bookingStatus)?.label || 'Active (exclude cancelled)'],
      ['Total booking hours', bookingHours],
      ['Activity', activityType],
      ['Total rental', report.summary?.totalRental ?? 0],
      ['Hourly amounts', 'Hourly rental follows recorded court/time rates, with discounts allocated proportionally and totals preserved. Older bookings use current rules for reconstruction.'],
      [],
      ['Purchased date', 'Purchased time', 'Date', 'Start time', 'End time', 'Type', 'Session', 'Hosted by', 'Venue', 'Court', 'Player', 'Email', 'Phone', 'Booking status', 'Payment method', 'Payment status', 'Collection', 'Funds location', 'Funds status', 'Payout reference', 'Rental amount', 'Hourly pricing basis', ...(showConvenienceFee ? ['Convenience Fee'] : []), 'Online venue earnings', 'Payment reference', 'Transaction ID', 'Group payment'],
      ...itemizedBookings.map((booking) => [
        formatDate(booking.purchasedAt), formatTime(booking.purchasedAt),
        formatDate(booking.startTime), formatTime(booking.startTime), formatTime(booking.endTime),
        booking.bookingType, booking.openPlayName, booking.organizerType ? `${booking.organizerType}: ${booking.organizerName || '—'}` : '', booking.venueName, courtDisplayName(booking.courtName), booking.playerName, booking.playerEmail,
        booking.playerPhoneNumber, booking.status, booking.paymentMethod, booking.paymentStatus,
        booking.settlementLabel, booking.fundsLocation, booking.fundsStatus, booking.payoutReference, booking.rentalAmount, booking.hourlyPricingBasis || 'Recorded total',
        ...(showConvenienceFee ? [booking.convenienceFeeCoveredByVenue ? 'Covered by venue' : ''] : []),
        booking.payableByFreac, booking.paymentReference, booking.transactionId, booking.isGroupPayment ? 'Yes' : 'No',
      ]),
    ];
    const csv = `\uFEFF${rows.map((row) => row.map(csvCell).join(',')).join('\n')}`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `freac-booking-details-${dateRange.start}-to-${dateRange.end}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const summary = report?.summary || {};
  const summaryCards = [
    { label: 'Total Booking Hours', value: `${bookingHours.toLocaleString('en-US', { maximumFractionDigits: 2 })} hrs`, detail: 'Court booking duration across all selected courts; excludes Open Play and Step Dance registrations', icon: CalendarDays, background: 'bg-[#E8F4FD]', color: 'text-[#264D59]' },
    { label: 'Total rental', value: formatCurrency(summary.totalRental), detail: 'Rental amount for the displayed bookings', icon: CircleDollarSign, background: 'bg-[#E0F7F4]', color: 'text-[#0B7F83]' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Booking Details Report</h1>
          <p className="mt-1 text-sm text-[#64748B]">Review hourly court booking items and Open Play and Step Dance registrations for a selected date range.</p>
        </div>
        <div className="flex flex-wrap gap-2"><div className="flex rounded-2xl border border-[#CBD5E1] bg-white p-1"><button type="button" onClick={() => changeView('matrix')} className={`flex h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold ${viewMode === 'matrix' ? 'bg-[#264D59] text-white' : 'text-[#64748B]'}`}><Grid3X3 size={16}/>Court schedule</button><button type="button" onClick={() => changeView('table')} className={`flex h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold ${viewMode === 'table' ? 'bg-[#264D59] text-white' : 'text-[#64748B]'}`}><List size={16}/>Booking list</button></div><button type="button" onClick={exportReport} disabled={!report} className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50"><Download className="mr-2 h-4 w-4" /> Export CSV</button></div>
      </div>

      <section className="card p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1F2937]"><Filter className="h-4 w-4 text-[#0B7F83]" /> Report filters</div>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-7">
          <label className="text-xs font-medium text-[#64748B]">Venue
            <select value={venueId} onChange={(event) => { setVenueId(event.target.value); setCourtId(''); }} className="input-field mt-1">
              {viewMode === 'table' && <option value="">All venues</option>}
              {venues.map((venue) => <option key={venue.id} value={venue.id}>{venue.name}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[#64748B]">Activity
            <select aria-label="Activity" value={activityType} onChange={e => setActivityType(e.target.value)} className="input-field mt-1"><option value="All">All activities</option><option value="CourtBookings">Court Bookings</option><option value="OpenPlay">Open Play</option><option value="StepDance">Step Dance</option></select>
          </label>
          <label className="text-xs font-medium text-[#64748B]">Payment method
            <select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)} className="input-field mt-1">
              <option value="">All payment methods</option>
              {paymentMethods.map((method) => <option key={method} value={method}>{method}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[#64748B]">Booking status
            <select value={bookingStatus} onChange={(event) => setBookingStatus(event.target.value)} className="input-field mt-1">
              {bookingStatuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[#64748B]">Court
            <select value={courtId} onChange={(event) => setCourtId(event.target.value)} className="input-field mt-1">
              <option value="">All courts</option>
              {filteredCourts.map((court) => <option key={court.id} value={court.id}>{courtDisplayName(court.name)}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-[#64748B]">Start date
            <input type="date" value={dateRange.start} max={dateRange.end} onChange={(event) => setDateRange((current) => ({ ...current, start: event.target.value }))} className="input-field mt-1" />
          </label>
          <label className="text-xs font-medium text-[#64748B]">End date
            <input type="date" value={dateRange.end} min={dateRange.start} onChange={(event) => setDateRange((current) => ({ ...current, end: event.target.value }))} className="input-field mt-1" />
          </label>
          <div className="flex gap-2 self-end"><button type="button" onClick={showToday} className="btn-secondary">Today</button><button type="button" onClick={() => loadReport()} className="btn-primary">Apply filters</button></div>
        </div>
      </section>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" /></div>
      ) : !report ? null : (
        <>
          <div className="grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
            {summaryCards.map((card) => <div key={card.label} className="card p-5"><div className="flex items-start gap-4"><div className={`rounded-xl p-3 ${card.background}`}><card.icon className={`h-6 w-6 ${card.color}`} /></div><div><p className="text-sm text-[#64748B]">{card.label}</p><p className="mt-1 text-xl font-bold text-[#1F2937]">{card.value}</p><p className="mt-1 text-xs text-[#94A3B8]">{card.detail}</p></div></div></div>)}
          </div>

          <section className="card overflow-hidden relative">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#E2E8F0] px-5 py-4"><div><h2 className="font-semibold text-[#1F2937]">{viewMode === 'matrix' ? 'Court schedule matrix' : 'Booking records'}</h2><p className="mt-1 text-xs text-[#64748B]">{viewMode === 'matrix' ? `Period: ${formatDate(`${dateRange.start}T00:00:00`)}–${formatDate(`${dateRange.end}T00:00:00`)} · Each cell shows player, booking/payment status, payment method, and rental amount.` : `${itemizedBookings.length} itemised row${itemizedBookings.length === 1 ? '' : 's'} from ${report.bookings?.length || 0} booking record(s).`}</p></div></div>
            <p className="border-b border-[#E2E8F0] bg-[#F8FAFC] px-5 py-3 text-xs text-[#64748B]">Multi-hour court bookings are shown in hourly items. Rental follows recorded court/time rates with proportional discounts. Older bookings are reconstructed using current rules; total booking hours sums the original court booking durations. Session entries remain per registration; group entries share one transaction reference.</p>
            {viewMode === 'matrix' ? <CourtSchedule bookings={itemizedBookings} courts={filteredCourts} onOpenBooking={(id) => navigate(`/bookings/${id}`)} /> : <>
            <div className="overflow-x-auto">
              <table className={`${showConvenienceFee ? 'min-w-[1940px]' : 'min-w-[1800px]'} w-full text-left text-sm`}>
                <thead className="bg-[#F8FAFC] text-xs uppercase tracking-wide text-[#64748B]">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Purchased at</th><th className="px-5 py-3 font-semibold">Booking date & time</th><th className="px-5 py-3 font-semibold">Type</th><th className="px-5 py-3 font-semibold">Hosted by</th><th className="px-5 py-3 font-semibold">Venue / Court</th><th className="px-5 py-3 font-semibold">Player</th><th className="px-5 py-3 font-semibold">Status</th><th className="px-5 py-3 font-semibold">Payment</th><th className="px-5 py-3 font-semibold">Collection</th><th className="px-5 py-3 font-semibold">Funds</th><th className="px-5 py-3 font-semibold">Rental</th>{showConvenienceFee && <th className="px-5 py-3 font-semibold">Convenience Fee</th>}<th className="px-5 py-3 font-semibold">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {itemizedBookings.length ? itemizedBookings.map((booking) => <tr key={booking.itemKey} className="hover:bg-[#F8FAFC]">
                    <td className="whitespace-nowrap px-5 py-4"><p className="font-medium text-[#1F2937]">{formatDate(booking.purchasedAt)}</p><p className="text-xs text-[#64748B]">{formatTime(booking.purchasedAt)}</p></td>
                    <td className="whitespace-nowrap px-5 py-4"><p className="font-medium text-[#1F2937]">{formatDate(booking.startTime)}</p><p className="text-xs text-[#64748B]">{formatTime(booking.startTime)} – {formatTime(booking.endTime)}</p></td>
                    <td className="px-5 py-4"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${booking.bookingType !== 'Court Booking' ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-700'}`}>{booking.bookingType || 'Court Booking'}</span>{booking.isGroupPayment && <small className="mt-1 block text-[#64748B]">Group · {booking.transactionId}</small>}</td>
                    <td className="px-5 py-4">{booking.organizerType ? <><p className={`font-medium ${booking.organizerType === 'Player organiser' ? 'text-violet-700' : 'text-[#1F2937]'}`}>{booking.organizerType}</p><p className="text-xs text-[#64748B]">{booking.organizerName || '—'}</p></> : <span className="text-[#94A3B8]">—</span>}</td>
                    <td className="px-5 py-4"><p className="font-medium text-[#1F2937]">{booking.venueName}</p><p className="text-xs text-[#64748B]">{booking.openPlayName ? `${booking.openPlayName} · ${courtDisplayName(booking.courtName)}` : courtDisplayName(booking.courtName)}</p></td>
                    <td className="px-5 py-4"><p className="font-medium text-[#1F2937]">{booking.playerName || '—'}</p><p className="text-xs text-[#64748B]">{booking.playerEmail}</p><p className="text-xs text-[#64748B]">{booking.playerPhoneNumber || ''}</p></td>
                    <td className="px-5 py-4"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(booking.status)}`}>{booking.status}</span></td>
                    <td className="px-5 py-4"><p className="font-medium text-[#344054]">{booking.paymentMethod || '—'}</p><p className="text-xs text-[#64748B]">{booking.paymentStatus}</p></td>
                    <td className="whitespace-nowrap px-5 py-4"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${booking.collectionChannel === 'VenueDirect' ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#E8F4FD] text-[#264D59]'}`}>{booking.settlementLabel}</span></td>
                    <td className="px-5 py-4"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${booking.fundsLocation === 'VenueOwner' ? 'bg-[#DCFCE7] text-[#166534]' : booking.fundsLocation === 'FREAC' ? 'bg-[#E8F4FD] text-[#264D59]' : booking.fundsLocation === 'PayMongo' ? 'bg-[#FFF4E5] text-[#B45309]' : 'bg-[#F1F5F9] text-[#475569]'}`}>{booking.fundsLocation}</span><p className="mt-1 max-w-[220px] text-xs text-[#64748B]">{booking.fundsStatus}</p>{booking.payoutReference && <p className="mt-1 max-w-[180px] truncate font-mono text-[10px] text-[#64748B]" title={booking.payoutReference}>Payout: {booking.payoutReference}</p>}</td>
                    <td className="whitespace-nowrap px-5 py-4">{formatCurrency(booking.rentalAmount)}{booking.hourlyPricingBasis === 'Current rules' && <p className="text-xs text-[#64748B]">Reconstructed from current rules</p>}</td>{showConvenienceFee && <td className="whitespace-nowrap px-5 py-4">{booking.convenienceFeeCoveredByVenue ? 'Covered by venue' : formatCurrency(booking.convenienceFee || 0)}</td>}<td className="max-w-[180px] truncate px-5 py-4 font-mono text-xs text-[#64748B]" title={booking.paymentReference || ''}>{booking.paymentReference || '—'}</td>
                  </tr>) : <tr><td colSpan={showConvenienceFee ? 13 : 12} className="px-5 py-12 text-center text-sm text-[#64748B]">No bookings or Open Play and Step Dance registrations match these filters.</td></tr>}
                </tbody>
              </table>
            </div>
            </>}
          </section>
        </>
      )}
    </div>
  );
}
